/**
 * Everything you can change about yourself, drawn.
 *
 * ## The one idea in this file
 *
 * **The second body was never a body — it was a hairstyle.** Every row where the female character
 * differed from the male one was a row with hair in it, and every difference was *additive*: the
 * hair took columns 1-3 and 12-14, which the male silhouette leaves empty, and touched nothing
 * else. So there are not two bodies here. There is one bare-scalp body in `sprites.ts` and ten
 * hairstyles over it, of which the first two are exactly the two heads the game has always drawn.
 *
 * `tests/izgled.test.ts` composes those two against the original art and asserts pixel equality,
 * because "nobody's character changes" is a promise this refactor is only allowed to make if it can
 * be checked. Everything the change buys follows from collapsing that one axis: any hairstyle works
 * on any character, a new one costs three patches instead of six, and the walk cycle — the hardest
 * won thing in this codebase — stayed exactly where it was.
 *
 * ## The contract every patch here obeys
 *
 * A patch is `row index in body space (1-13) → a 16-character row`, where `.` means "keep whatever
 * is underneath". Four rules, all enforced by tests:
 *
 *   1. **Row 0 is forbidden.** The walk bob drops the body's empty top row, so anything drawn there
 *      is clipped off on every other step.
 *   2. **Hair owns rows 1-10, the outfit owns rows 9-13**, and where they meet the outfit wins,
 *      because hair falling past the collar is drawn behind a jacket, not through it.
 *   3. **Nothing may touch rows 11-13 columns 2-3 or 12-13.** Those are the hands. A hand that
 *      disappears under a hairstyle reads as a missing arm on every frame of the walk.
 *   4. **Souvenirs are applied last**, so an earned cap beats a hairstyle and headphones hang over
 *      a beard. That ordering is in `heroPose`, and it is the reason none of the art here has to
 *      know that souvenirs exist.
 *
 * ## Why these axes and not others
 *
 * At sixteen pixels a face is about four decisions (see `characters.ts`). Nine axes sounds like a
 * lot against that, and it is only tolerable because six of them are *shapes you can see at map
 * scale* — a silhouette with an afro is a different person forty pixels away — and three are
 * colours, which are the only thing that survives at forty pixels at all. Anything subtler than
 * that was tried and cut: eyebrow shape, nose, mouth, freckles. None of them resolved.
 */

import { palette } from './palette';

/** Row index in body space (1-13) → the 16-character patch. `.` keeps whatever is underneath. */
export type Patch = Record<number, string>;

/** One option on one axis: the same drawing seen three ways, plus what to call it in the picker. */
export interface Feature {
  /** Serbian, lower case, for the label under a thumbnail. The picker is pictures first. */
  label: string;
  down: Patch;
  up: Patch;
  side: Patch;
}

/* ================================================================== hair */

/**
 * Ten hairstyles.
 *
 * Order is frozen only at the first two — `kratka` is option 0 because option 0 of every axis has
 * to be the plainest thing on it (a value from before an axis existed decodes to 0), and `duga` is
 * option 1 because `core/look.ts` maps the four old female looks onto it by name.
 *
 * The rest are ordered by how much room they take up, which is also roughly how loud they are.
 */
export const HAIR: readonly Feature[] = [
  {
    // The short crop. Rows 2-3 are the whole crown, and the single pixels at rows 4-7 are the
    // sideburn and the shaded far temple — remove them and the head reads as a wig sitting on top.
    label: 'kratka',
    down: {
      2: '...kkhhhhhhkk...',
      3: '..khhhhhhhhhhk..',
      4: '...h........H...',
      5: '...h........H...',
      6: '...h........H...',
      7: '....h......h....',
    },
    up: {
      2: '...kkhhhhhhkk...',
      3: '..khhhhhhhhhhk..',
      4: '...hhhhhhhhhH...',
      5: '...hhhhhhhhhH...',
      6: '...hhhhhhhhhH...',
      7: '....hhhhhhhh....',
      8: '.....hhhhhh.....',
    },
    side: {
      2: '....khhhhhk.....',
      3: '...khhhhhhhk....',
      4: '....hhhh........',
      5: '....hhh.........',
      6: '....hhh.........',
      7: '.....hh.........',
    },
  },
  {
    // Long, to the shoulders and no further. One row lower is where the hands are, and a hand
    // hidden behind hair reads as a missing arm on every frame of the walk.
    label: 'duga',
    down: {
      2: '...kkhhhhhhkk...',
      3: '..khhhhhhhhhhk..',
      4: '.khh........HHk.',
      5: '.khh........HHk.',
      6: '.khh........HHk.',
      7: '.khhh......hHHk.',
      8: '.khh........HHk.',
      9: '.khh........HHk.',
      10: '.kh..........Hk.',
    },
    up: {
      2: '...kkhhhhhhkk...',
      3: '..khhhhhhhhhhk..',
      4: '.khhhhhhhhhhhHk.',
      5: '.khhhhhhhhhhhHk.',
      6: '.khhhhhhhhhhhHk.',
      7: '.khhhhhhhhhhhHk.',
      8: '.khhhhhhhhhhhHk.',
      9: '.khh........HHk.',
      10: '.kh..........Hk.',
    },
    side: {
      2: '....khhhhhk.....',
      3: '...khhhhhhhk....',
      4: '..khhhhh........',
      5: '..khhhh.........',
      6: '.khhhhh.........',
      7: '.khhhhh.........',
      8: '.khhh...........',
      9: '.khhh...........',
      10: '.khh............',
    },
  },
  {
    // Jaw length, with a flare at rows 5-6 that is the entire difference between "wavy" and "a bob".
    // Two pixels of width, and they only read because they sit outside the head's own outline.
    label: 'talasasta',
    down: {
      2: '...kkhhhhhhkk...',
      3: '..khhhhhhhhhhk..',
      4: '.khh........HHk.',
      5: 'khhh........HHHk',
      6: 'khhh........HHHk',
      7: '.khhh......hHHk.',
      8: '..kh........Hk..',
    },
    up: {
      2: '...kkhhhhhhkk...',
      3: '..khhhhhhhhhhk..',
      4: '.khhhhhhhhhhhHk.',
      5: 'khhhhhhhhhhhhhHk',
      6: 'khhhhhhhhhhhhhHk',
      7: '.khhhhhhhhhhhHk.',
      8: '..khhhhhhhhhHk..',
    },
    side: {
      2: '....khhhhhk.....',
      3: '...khhhhhhhk....',
      4: '..khhhhh........',
      5: '.khhhhh.........',
      6: '.khhhhh.........',
      7: '..khhhh.........',
      8: '..khh...........',
    },
  },
  {
    // Pulled back. From the front the tell is the hairline: row 3 stops two columns short of the
    // outline on each side, so the temples show. Without that it would be the short crop again,
    // and two options that draw the same thumbnail are one option and a bug.
    label: 'rep',
    down: {
      2: '...kkhhhhhhkk...',
      // Inset by one column with its own edge, rather than simply stopping short: a hairline that
      // ends in bare skin against the head's outline reads as a bald patch, not as tied-back hair.
      3: '...khhhhhhhhk...',
    },
    up: {
      2: '...kkhhhhhhkk...',
      3: '..khhhhhhhhhhk..',
      4: '...hhhhhhhhhH...',
      5: '...hhhhhhhhhH...',
      6: '...hhhhhhhhhH...',
      7: '....hhhhhhhh....',
      // The nape, then the tail down the back. Both fill the body's own outline rather than
      // drawing a narrower shape inside it — a patch that stops short of the silhouette leaves the
      // base showing at the edges, which is a tail with a rind of neck around it.
      8: '.....hhhhhh.....',
      9: '.......hh.......',
      10: '.......hh.......',
    },
    side: {
      2: '....khhhhhk.....',
      3: '...khhhhhhhk....',
      4: '..khhhhh........',
      5: 'khhk............',
      6: 'khhk............',
      7: '.khhk...........',
      8: '..kk............',
    },
  },
  {
    // Tied up. The knot is on the back of the head, so the front view carries it with a centre
    // parting instead — the one shape that says "this hair is going somewhere" head-on.
    label: 'punđa',
    // Volume gathered upward: the crown is a row taller and smoother than the crop's, and two loose
    // strands come down past the ear. The knot itself is behind the head and is drawn with the
    // highlight rather than with an outline — an outlined circle on the back of a head is a
    // doughnut, which is exactly what the first attempt looked like.
    down: {
      1: '....kkkkkkkk....',
      2: '...khhhhhhhhk...',
      3: '..khhhhhhhhhhk..',
      4: '..khh......hHk..',
      5: '...h........H...',
      6: '...h........H...',
    },
    up: {
      1: '....kkkkkkkk....',
      2: '...khLLLLLLhk...',
      3: '..khhLLLLLLhhk..',
      4: '..khhhLLLLhhhk..',
      5: '..khhhhhhhhhHk..',
      6: '..khhhhhhhhhHk..',
      7: '...khhhhhhhhk...',
      8: '....khhhhhhk....',
    },
    side: {
      2: '..kLLhhhhhk.....',
      3: '.kLLLhhhhhhk....',
      4: '.kLLLhhhh.......',
      5: '..kkhhh.........',
      6: '...khhh.........',
      7: '....khh.........',
    },
  },
  {
    // Coils. The only style that breaks the head's silhouette on every side, which is why it is
    // textured with the shade key rather than drawn flat — a solid blob this big reads as a helmet.
    label: 'kovrdžava',
    down: {
      1: '....kkkkkkkk....',
      2: '..kkhHhhhhHhkk..',
      3: '.khHhhhhhhhhHhk.',
      4: 'khHh........hHhk',
      5: 'khhH........Hhhk',
      6: 'khHh........hHhk',
      7: '.khh.......hhHk.',
      8: '..kh........Hk..',
    },
    up: {
      1: '....kkkkkkkk....',
      2: '..kkhHhhhhHhkk..',
      3: '.khHhhhhhhhhHhk.',
      4: 'khHhhhhhhhhhhHhk',
      5: 'khhHhhhhhhhhHhhk',
      6: 'khHhhhhhhhhhhHhk',
      7: '.khhhhhhhhhhhhk.',
      8: '..khhhhhhhhhHk..',
    },
    side: {
      1: '...kkkkkkk......',
      2: '..kHhhhhhhk.....',
      3: '.khHhhhhhhhk....',
      4: 'khHhhhhh........',
      5: 'khhHhhh.........',
      6: 'khHhhhh.........',
      7: '.khhh...........',
      8: '..khh...........',
    },
  },
  {
    // A side part with the fringe swept across the forehead, lit from the top left. The highlight
    // key is doing real work here: without it a pale side part reads as a bald patch.
    label: 'razdeljak',
    down: {
      2: '..kkhLLLhhhhkk..',
      3: '..khLLhhhhhhhk..',
      4: '..khhhhhhhhhHk..',
      5: '...h........H...',
      6: '...h........H...',
      7: '....h......h....',
    },
    up: {
      2: '...kkhLLLhhkk...',
      3: '..khLLhhhhhhhk..',
      4: '..khhhhhhhhhHk..',
      5: '..khhhhhhhhhHk..',
      6: '..khhhhhhhhhHk..',
      7: '...khhhhhhhhk...',
      8: '....khhhhhhk....',
    },
    side: {
      2: '....khhhhhk.....',
      3: '...khLLhhhhk....',
      4: '...khhhhh.......',
      5: '...khhh.........',
      6: '...khhh.........',
      7: '....khh.........',
    },
  },
  {
    // Shaved sides, strip down the middle. The sides are the shade key rather than bare skin,
    // because a buzzed head is scalp seen *through* hair and drawing it as skin gives a bald man
    // with a tuft.
    label: 'iroquois',
    down: {
      2: '......hhhh......',
      3: '...QQhhhhhhQQ...',
      4: '...Q........Q...',
    },
    up: {
      2: '......hhhh......',
      3: '...QQhhhhhhQQ...',
      4: '..kQQhhhhhhQQk..',
      5: '..kQQQhhhhQQQk..',
      6: '..kQQQQQQQQQQk..',
      7: '...kQQQQQQQQk...',
      8: '....kQQQQQQk....',
    },
    side: {
      2: '....khhhhhk.....',
      3: '...kQhhhhhQk....',
      4: '...kQQQQ........',
      5: '...kQQQ.........',
      6: '...kQQQ.........',
      7: '....kQQ.........',
    },
  },
  {
    // Locs. Same length as `duga` and it has to read differently at map scale, so the whole length
    // alternates between the hair colour and its shade — texture, not shape.
    label: 'pletenice',
    down: {
      2: '...kkhHhhHhkk...',
      3: '..khHhhhhhhHhk..',
      4: '.khH........Hhk.',
      5: '.kHh........hHk.',
      6: '.khH........Hhk.',
      7: '.kHhh......hhHk.',
      8: '.khH........Hhk.',
      9: '.kHh........hHk.',
      10: '.khH........Hhk.',
    },
    up: {
      2: '...kkhHhhHhkk...',
      3: '..khHhhhhhhHhk..',
      4: '.khHhhhhhhhhHhk.',
      5: '.kHhhhhhhhhhhHk.',
      6: '.khHhhhhhhhhHhk.',
      7: '.kHhhhhhhhhhhHk.',
      8: '.khHhhhhhhhhHhk.',
      9: '.kHh........hHk.',
      10: '.khH........Hhk.',
    },
    side: {
      2: '....khhhhhk.....',
      3: '...khHhhhhhk....',
      4: '..khHhhhh.......',
      5: '.kHhhhh.........',
      6: '.khHhhh.........',
      7: '.kHhhh..........',
      8: '.khHh...........',
      9: '.kHh............',
      10: '.khH............',
    },
  },
  {
    // Shaved. Deliberately no art at all: the bare body already carries a shaded temple, and every
    // pixel added here made it look like a swimming cap rather than a head.
    label: 'ćelav',
    down: {},
    up: {},
    side: {},
  },
];

/* ================================================================== facial hair */

/**
 * Six. Drawn in `q` and `Q` rather than the hair keys, and that is not a detail — see
 * `characters.ts`: a moustache the same value as the hair above it merges with the head at this
 * size, and the same value as the outline reads as a hole in the face. `heroColors` puts `q` in
 * between and `Q` a shade lighter still, which is what stubble is.
 */
export const BEARD: readonly Feature[] = [
  { label: 'bez brade', down: {}, up: {}, side: {} },
  {
    label: 'strnjika',
    down: { 7: '.....QQQQQQ.....', 8: '.....QQQQQQ.....' },
    up: {},
    side: { 7: '.......QQQQ.....', 8: '......QQQQ......' },
  },
  {
    // Two pixels wide at the chin. The width is the whole difference between a goatee and a beard.
    label: 'kozja',
    down: { 7: '.....qqqqqq.....', 8: '......qqqq......' },
    up: {},
    side: { 7: '.......qqqq.....', 8: '.......qq.......' },
  },
  {
    label: 'puna',
    down: { 6: '...q........q...', 7: '...qqqqqqqqqq...', 8: '....qqqqqqqq....' },
    up: {},
    side: { 6: '.......qqqqq....', 7: '.....qqqqqqq....', 8: '.....qqqqqq.....' },
  },
  {
    label: 'brkovi',
    down: { 7: '.....qqqqqq.....' },
    up: {},
    side: { 7: '........qqq.....' },
  },
  {
    label: 'duga brada',
    down: {
      6: '...q........q...',
      7: '...qqqqqqqqqq...',
      8: '....qqqqqqqq....',
      9: '.....qqqqqq.....',
      10: '......qqqq......',
    },
    up: {},
    side: {
      6: '.......qqqqq....',
      7: '.....qqqqqqq....',
      8: '.....qqqqqq.....',
      9: '.....qqqqq......',
      10: '.....qqqq.......',
    },
  },
];

/* ================================================================== outfits */

/**
 * Seven shirts, all of them variations painted onto the same torso.
 *
 * `C` is the shirt's own shade, `w` is white and `s` is skin, and between the three of them you can
 * say stripes, a hood, an open jacket, buttons, bare arms and a print without the outfit ever
 * needing a colour of its own. That matters more than it sounds: an outfit with an independent
 * second colour would be a tenth axis, and the tenth axis is the one that turns a wardrobe into
 * a form.
 */
export const OUTFIT: readonly Feature[] = [
  { label: 'majica', down: {}, up: {}, side: {} },
  {
    label: 'pruge',
    down: { 10: '....CCCCCCCC....', 12: '....CCCCCCCC....' },
    up: { 10: '....CCCCCCCC....', 12: '....CCCCCCCC....' },
    side: { 10: '.....CCCCC......', 12: '.....CCCCCC.....' },
  },
  {
    // The drawstrings are two white pixels and they are the entire reason this reads as a hoodie
    // rather than as a shirt with a dark collar.
    label: 'duks',
    down: { 8: '.....CCCCCC.....', 9: '.....CCCCCC.....', 10: '.......ww.......', 12: '.....CCCCCC.....' },
    up: { 8: '.....CCCCCC.....', 9: '....CCCCCCCC....', 10: '.....CCCCCC.....' },
    side: { 8: '......CCCC......', 9: '.....CCCCC......', 10: '......ww........' },
  },
  {
    // The front opens. Drawn in the outline key rather than in the shirt's own shade, because a
    // shade-on-shade seam at this size is a crease in a t-shirt, not a jacket.
    label: 'jakna',
    down: {
      9: '.....CCkkCC.....',
      10: '.......kk.......',
      11: '.......kk.......',
      12: '.......kk.......',
      13: '.......kk.......',
    },
    up: { 9: '.....CCCCCC.....', 10: '...CCCCCCCCCC...' },
    side: { 9: '.....CCCCk......', 11: '..........k.....', 12: '..........k.....', 13: '.........k......' },
  },
  {
    label: 'košulja',
    down: { 9: '.....CCwwCC.....', 11: '.......w........', 13: '.......w........' },
    up: { 9: '.....CCCCCC.....' },
    side: { 9: '.....CCCCC......', 11: '.........w......', 13: '........w.......' },
  },
  {
    // Bare arms. Skin has to come back in at the shoulder columns, which is why every outfit patch
    // is allowed the skin key at all.
    label: 'bez rukava',
    // The arm *is* the torso's outer column — there is no separate arm sprite — so a sleeveless top
    // is that column in skin, all the way down to the hand. The first attempt put skin across the
    // top of the chest as well and produced a bib.
    down: { 10: '....s......sS...', 11: '....s......sS...', 12: '....s......sS...', 13: '....s......sS...' },
    up: { 10: '....s......sS...', 11: '....s......sS...', 12: '....s......sS...', 13: '....s......sS...' },
    side: { 10: '.....s..........', 11: '.....s..........', 12: '.....s..........' },
  },
  {
    label: 'print',
    down: { 11: '.......ww.......', 12: '......wwww......' },
    up: {},
    side: { 11: '........ww......', 12: '........ww......' },
  },
];

/* ================================================================== extras */

/**
 * Five, and one at a time.
 *
 * A second slot was drawn and thrown away: glasses plus an earring plus a headband is three things
 * to understand and, at sixteen pixels, one shape you cannot read. Souvenirs are the exception and
 * they are allowed to be, because a souvenir was earned and is therefore worth the pixel.
 */
export const EXTRA: readonly Feature[] = [
  { label: 'ništa', down: {}, up: {}, side: {} },
  {
    // Frame, lens glint, eye, bridge, eye, glint, frame — the whole thing on one row, because a
    // two-row spectacle at this size is a blindfold.
    label: 'naočare',
    down: { 4: '.....kk..kk.....', 5: '...kkfikkifkk...' },
    up: {},
    side: { 4: '.......kk.......', 5: '....kkkfik......' },
  },
  {
    label: 'sunčane',
    down: { 4: '.....kk..kk.....', 5: '...kkkkkkkkkk...' },
    up: {},
    side: { 4: '.......kk.......', 5: '....kkkkkk......' },
  },
  {
    // Drawn after the hair, so it shows on somebody with long hair instead of being buried by it.
    label: 'minđuša',
    down: { 6: '...y........y...' },
    up: { 6: '...y........y...' },
    side: { 6: '....y...........' },
  },
  {
    label: 'marama',
    down: { 3: '..kaaaaaaaaaak..', 4: '..kA........Ak..' },
    up: { 3: '..kaaaaaaaaaak..', 4: '..kA........Ak..', 5: '.....kaak.......' },
    side: { 3: '...kaaaaaaak....', 4: '.kakA.....A.....' },
  },
];

/* ================================================================== the picker's own labels */

/**
 * What the editor calls each axis, and which tab it sits under.
 *
 * The tabs exist for one reason: nine rows of thumbnails on one screen is the exact failure mode
 * this whole product was built against (CLAUDE.md — "an app built to cure overwhelm becomes
 * overwhelming itself"). Three tabs of three is a wardrobe. One screen of nine is a form.
 */
export const SWATCHES = {
  skin: palette.skin,
  hairColor: palette.hair,
  outfitColor: palette.shirt,
  trousers: palette.trousers,
  eyes: palette.eyes,
} as const;
